/* eslint-disable jsdoc/require-jsdoc -- 私有响应守卫由紧邻命名和仓储边界测试约束。 */
import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  isStayResourceGeoCandidateRaw,
  isStayResourceGeoReviewResultRaw,
  isStayResourceGeoReviewAuditRaw,
  isStayResourceGeoVerificationResultRaw,
  isStayResourceHousLevelRaw,
  isStayResourceLegacyMappingRaw,
  isStayResourceListPageRaw,
  isStayResourceMediaRaw,
  isStayResourceSkuRaw,
  isStayResourceSpuRaw,
  isStayResourceSupplierRaw,
  isStayResourceSupplierOptionRaw,
  mapStayResourceGeoCandidate,
  mapStayResourceGeoReview,
  mapStayResourceGeoReviewAudit,
  mapStayResourceGeoReviewResult,
  mapStayResourceGeoVerificationResult,
  mapStayResourceHousLevel,
  mapStayResourceListPage,
  mapStayResourceListParams,
  mapStayResourceLegacyMapping,
  mapStayResourceMedia,
  mapStayResourceMediaMutation,
  mapStayResourceMediaOrder,
  mapStayResourceSku,
  mapStayResourceSkuMutation,
  mapStayResourceSpu,
  mapStayResourceSpuMutation,
  mapStayResourceReview,
  mapStayResourceSupplier,
  mapStayResourceSupplierMutation,
  mapStayResourceSupplierOption,
} from "../api/adapters";
import type {
  StayResourceGeoCandidate,
  StayResourceGeoReviewMutation,
  StayResourceGeoReviewAuditItem,
  StayResourceGeoReviewResult,
  StayResourceGeoVerificationResult,
  StayResourceHousLevelOption,
  StayResourceListPage,
  StayResourceListParams,
  StayResourceReviewMutation,
  StayResourceLegacyMappingItem,
  StayResourceMediaItem,
  StayResourceMediaMutation,
  StayResourceMediaOrderItem,
  StayResourceMediaUploadResult,
  StayResourceSkuItem,
  StayResourceSkuMutation,
  StayResourceSpuItem,
  StayResourceSpuMutation,
  StayResourceSupplierItem,
  StayResourceSupplierMutation,
  StayResourceSupplierOption,
} from "../types";

const noRetry = { "axios-retry": { retries: 0 } } as const;
const geoVerifyRequest = { timeout: 60_000, ...noRetry } as const;

/** 住宿写接口的唯一 URL 事实源，便于契约落地后集中校正。 */
export const stayResourceEndpoints = {
  spus: "/resource-stays",
  spu: (spuId: string) => `/resource-stays/${spuId}`,
  spuReview: (spuId: string) => `/resource-stays/${spuId}/review`,
  legacyMappings: (spuId: string) => `/resource-stays/${spuId}/legacy-mappings`,
  spuStatus: (spuId: string, action: "enable" | "disable") =>
    `/resource-stays/${spuId}/${action}`,
  skus: (spuId: string) => `/resource-stays/${spuId}/skus`,
  sku: (spuId: string, skuId: string) => `/resource-stays/${spuId}/skus/${skuId}`,
  skuStatus: (spuId: string, skuId: string, action: "enable" | "disable") =>
    `/resource-stays/${spuId}/skus/${skuId}/${action}`,
  media: (spuId: string) => `/resource-stays/${spuId}/media`,
  mediaUpload: "/resource-stays/media/upload",
  housLevels: "/suppliers/dim-hous-levels",
  mediaItem: (spuId: string, mediaId: string) =>
    `/resource-stays/${spuId}/media/${mediaId}`,
  mediaOrder: (spuId: string) => `/resource-stays/${spuId}/media/reorder`,
  mediaObsolete: (spuId: string, mediaId: string) =>
    `/resource-stays/${spuId}/media/${mediaId}/obsolete`,
  geoCandidates: (spuId: string) => `/resource-stays/${spuId}/geo/candidates`,
  geoVerify: (spuId: string) => `/resource-stays/${spuId}/geo/verify`,
  geoReview: (spuId: string) => `/resource-stays/${spuId}/geo/review`,
  geoReviews: (spuId: string) => `/resource-stays/${spuId}/geo/reviews`,
  suppliers: (spuId: string) => `/resource-stays/${spuId}/suppliers`,
  supplierOptions: "/resource-stays/supplier-options",
  supplierCapabilities: (spuId: string) => `/resource-stays/${spuId}/supplier-capabilities`,
  supplier: (spuId: string, capabilityId: string) =>
    `/resource-stays/${spuId}/supplier-capabilities/${capabilityId}`,
} as const;

/** 使用住宿媒体权限上传文件，不依赖供应商基础设置权限。 */
export async function uploadStayResourceMediaFile(
  file: File,
): Promise<StayResourceMediaUploadResult> {
  const kind = file.type.startsWith("image/")
    ? "image"
    : file.type.startsWith("video/") ? "file" : undefined;
  if (!kind) throw new Error("住宿媒体仅支持图片或视频");
  const form = new FormData();
  form.append("file", file);
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    `${stayResourceEndpoints.mediaUpload}?kind=${kind}`,
    form,
    noRetry,
  );
  const result = requireData(
    response.data,
    "住宿媒体上传接口返回了无效数据",
    isMediaUploadResult,
  );
  return { key: result.key, fileUrl: result.file_url, mediaId: result.media_id };
}

/** 复用既有 dim_hous_level 住宿档次下拉数据源。 */
export async function fetchStayResourceHousLevels(): Promise<StayResourceHousLevelOption[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(stayResourceEndpoints.housLevels);
  return requireList(
    response.data,
    "住宿档次接口返回了无效数据",
    isStayResourceHousLevelRaw,
  ).map(mapStayResourceHousLevel);
}

/** 读取酒店主档的旧系统来源映射，只提供追溯展示。 */
export async function fetchStayResourceLegacyMappings(
  spuId: string,
): Promise<StayResourceLegacyMappingItem[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    stayResourceEndpoints.legacyMappings(spuId),
  );
  return requireList(
    response.data,
    "旧系统追溯接口返回了无效数据",
    isStayResourceLegacyMappingRaw,
  ).map(mapStayResourceLegacyMapping);
}

/** 分页读取酒店资源主档。 */
export async function fetchStayResources(
  params: StayResourceListParams,
): Promise<StayResourceListPage> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(stayResourceEndpoints.spus, {
    params: mapStayResourceListParams(params),
  });
  return mapStayResourceListPage(
    requireData(response.data, "房资源列表接口返回了无效数据", isStayResourceListPageRaw),
  );
}

/** 读取单个酒店资源详情。 */
export async function fetchStayResource(spuId: string): Promise<StayResourceSpuItem> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(stayResourceEndpoints.spu(spuId));
  return mapStayResourceSpu(
    requireData(response.data, "房资源详情接口返回了无效数据", isStayResourceSpuRaw),
  );
}

/** 创建酒店资源主档。 */
export async function createStayResource(
  payload: StayResourceSpuMutation,
): Promise<StayResourceSpuItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.spus,
    mapStayResourceSpuMutation(payload),
    { ...noRetry, timeout: 120_000 },
  );
  return mapStayResourceSpu(
    requireData(response.data, "房资源创建接口返回了无效数据", isStayResourceSpuRaw),
  );
}

/** 更新酒店资源主档。 */
export async function updateStayResource(
  spuId: string,
  payload: StayResourceSpuMutation,
): Promise<StayResourceSpuItem> {
  const response = await httpClient.put<ApiEnvelope<unknown>>(
    stayResourceEndpoints.spu(spuId),
    mapStayResourceSpuMutation(payload),
  );
  return mapStayResourceSpu(
    requireData(response.data, "房资源更新接口返回了无效数据", isStayResourceSpuRaw),
  );
}

/** 独立审核酒店主档材料，不复用业务启停或 GEO 审核。 */
export async function reviewStayResource(
  spuId: string,
  payload: StayResourceReviewMutation,
): Promise<StayResourceSpuItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.spuReview(spuId),
    mapStayResourceReview(payload),
    noRetry,
  );
  return mapStayResourceSpu(
    requireData(response.data, "房资源审核接口返回了无效数据", isStayResourceSpuRaw),
  );
}

/** 通过专用动作端点启用或停用酒店资源。 */
export async function setStayResourceDisabled(
  spuId: string,
  disabled: boolean,
  cascadeSkus = false,
): Promise<StayResourceSpuItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.spuStatus(spuId, disabled ? "disable" : "enable"),
    disabled ? { cascade_skus: cascadeSkus } : undefined,
    noRetry,
  );
  return mapStayResourceSpu(
    requireData(response.data, "房资源启停接口返回了无效数据", isStayResourceSpuRaw),
  );
}

/** 物理删除酒店主档及其本域子资源。 */
export async function deleteStayResource(spuId: string): Promise<{ staySpuId: string }> {
  const response = await httpClient.delete<ApiEnvelope<unknown>>(
    stayResourceEndpoints.spu(spuId),
    noRetry,
  );
  const data = requireData(
    response.data,
    "房资源删除接口返回了无效数据",
    isStayResourceDeleteRaw,
  );
  return { staySpuId: data.stay_spu_id };
}

function isStayResourceDeleteRaw(
  value: unknown,
): value is { stay_spu_id: string } {
  return Boolean(
    value
      && typeof value === "object"
      && typeof (value as { stay_spu_id?: unknown }).stay_spu_id === "string",
  );
}

/** 读取指定酒店的房型。 */
export async function fetchStayResourceSkus(spuId: string): Promise<StayResourceSkuItem[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(stayResourceEndpoints.skus(spuId));
  return requireList(response.data, "房型列表接口返回了无效数据", isStayResourceSkuRaw)
    .map(mapStayResourceSku);
}

/** 创建指定酒店的房型。 */
export async function createStayResourceSku(
  spuId: string,
  payload: StayResourceSkuMutation,
): Promise<StayResourceSkuItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.skus(spuId),
    mapStayResourceSkuMutation(payload),
    noRetry,
  );
  return mapStayResourceSku(
    requireData(response.data, "房型创建接口返回了无效数据", isStayResourceSkuRaw),
  );
}

/** 更新指定酒店的房型，并由 URL 固定父子归属。 */
export async function updateStayResourceSku(
  spuId: string,
  skuId: string,
  payload: StayResourceSkuMutation,
): Promise<StayResourceSkuItem> {
  const response = await httpClient.put<ApiEnvelope<unknown>>(
    stayResourceEndpoints.sku(spuId, skuId),
    mapStayResourceSkuMutation(payload),
  );
  return mapStayResourceSku(
    requireData(response.data, "房型更新接口返回了无效数据", isStayResourceSkuRaw),
  );
}

/** 通过专用动作端点启用或停用房型。 */
export async function setStayResourceSkuDisabled(
  spuId: string,
  skuId: string,
  disabled: boolean,
): Promise<StayResourceSkuItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.skuStatus(spuId, skuId, disabled ? "disable" : "enable"),
    {},
    noRetry,
  );
  return mapStayResourceSku(
    requireData(response.data, "房型启停接口返回了无效数据", isStayResourceSkuRaw),
  );
}

/** 读取指定酒店的展示媒体，并防御性排除证件治理记录。 */
export async function fetchStayResourceMedia(spuId: string): Promise<StayResourceMediaItem[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(stayResourceEndpoints.media(spuId));
  return requireList(response.data, "住宿媒体列表接口返回了无效数据", isStayResourceMediaRaw)
    .filter((raw) => raw.media_scene !== "certificate")
    .map(mapStayResourceMedia);
}

/** 创建住宿展示媒体元数据。 */
export async function createStayResourceMedia(
  spuId: string,
  payload: StayResourceMediaMutation,
): Promise<StayResourceMediaItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.media(spuId),
    mapStayResourceMediaMutation(payload),
    noRetry,
  );
  return mapStayResourceMedia(
    requireData(response.data, "住宿媒体创建接口返回了无效数据", isStayResourceMediaRaw),
  );
}

/** 更新住宿展示媒体元数据。 */
export async function updateStayResourceMedia(
  spuId: string,
  mediaId: string,
  payload: StayResourceMediaMutation,
): Promise<StayResourceMediaItem> {
  const response = await httpClient.put<ApiEnvelope<unknown>>(
    stayResourceEndpoints.mediaItem(spuId, mediaId),
    mapStayResourceMediaMutation(payload),
  );
  return mapStayResourceMedia(
    requireData(response.data, "住宿媒体更新接口返回了无效数据", isStayResourceMediaRaw),
  );
}

/** 原子更新酒店内媒体排序。 */
export async function reorderStayResourceMedia(
  spuId: string,
  items: StayResourceMediaOrderItem[],
): Promise<number> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.mediaOrder(spuId),
    mapStayResourceMediaOrder(items),
    noRetry,
  );
  const result = requireData(
    response.data,
    "住宿媒体排序接口返回了无效数据",
    isMediaOrderResult,
  );
  return result.updated_count;
}

/** 将媒体标记为 obsolete，保留历史元数据。 */
export async function obsoleteStayResourceMedia(
  spuId: string,
  mediaId: string,
): Promise<StayResourceMediaItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.mediaObsolete(spuId, mediaId),
    {},
    noRetry,
  );
  return mapStayResourceMedia(
    requireData(response.data, "住宿媒体作废接口返回了无效数据", isStayResourceMediaRaw),
  );
}

/** 读取 GEO 核验候选审计记录。 */
export async function fetchStayResourceGeoCandidates(
  spuId: string,
): Promise<StayResourceGeoCandidate[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    stayResourceEndpoints.geoCandidates(spuId),
  );
  return requireList(response.data, "GEO 候选接口返回了无效数据", isStayResourceGeoCandidateRaw)
    .map(mapStayResourceGeoCandidate);
}

/** 读取不可变的 GEO 自动与人工审核历史。 */
export async function fetchStayResourceGeoReviews(
  spuId: string,
): Promise<StayResourceGeoReviewAuditItem[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    stayResourceEndpoints.geoReviews(spuId),
  );
  return requireList(
    response.data,
    "GEO 审核历史接口返回了无效数据",
    isStayResourceGeoReviewAuditRaw,
  ).map(mapStayResourceGeoReviewAudit);
}

/** 仅触发房资源后端的统一 GEO 核验。 */
export async function verifyStayResourceGeo(
  spuId: string,
): Promise<StayResourceGeoVerificationResult> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.geoVerify(spuId),
    {},
    geoVerifyRequest,
  );
  return mapStayResourceGeoVerificationResult(
    requireData(
      response.data,
      "GEO 核验接口返回了无效数据",
      isStayResourceGeoVerificationResultRaw,
    ),
  );
}

/** 审核已有 GEO 候选，禁止提交自由坐标。 */
export async function reviewStayResourceGeo(
  spuId: string,
  payload: StayResourceGeoReviewMutation,
): Promise<StayResourceGeoReviewResult> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.geoReview(spuId),
    mapStayResourceGeoReview(payload),
    noRetry,
  );
  return mapStayResourceGeoReviewResult(
    requireData(
      response.data,
      "GEO 审核接口返回了无效数据",
      isStayResourceGeoReviewResultRaw,
    ),
  );
}

/** 从统一能力事实源读取指定酒店的供应商。 */
export async function fetchStayResourceSuppliers(
  spuId: string,
): Promise<StayResourceSupplierItem[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    stayResourceEndpoints.suppliers(spuId),
  );
  return requireList(response.data, "住宿供应关系列表接口返回了无效数据", isStayResourceSupplierRaw)
    .map(mapStayResourceSupplier);
}

/** 搜索可作为住宿履约主体的已启用供应商。 */
export async function fetchStayResourceSupplierOptions(
  query = "",
  limit = 50,
): Promise<StayResourceSupplierOption[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    stayResourceEndpoints.supplierOptions,
    { params: { q: query.trim() || undefined, limit } },
  );
  return requireList(
    response.data,
    "住宿供应商候选接口返回了无效数据",
    isStayResourceSupplierOptionRaw,
  ).map(mapStayResourceSupplierOption);
}

/** 创建酒店或房型级供应能力。 */
export async function createStayResourceSupplier(
  spuId: string,
  payload: StayResourceSupplierMutation,
): Promise<StayResourceSupplierItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    stayResourceEndpoints.supplierCapabilities(spuId),
    mapStayResourceSupplierMutation(payload),
    noRetry,
  );
  return mapStayResourceSupplier(
    requireData(response.data, "供应能力创建接口返回了无效数据", isStayResourceSupplierRaw),
  );
}

/** 更新统一供应能力。 */
export async function updateStayResourceSupplier(
  spuId: string,
  capabilityId: string,
  payload: StayResourceSupplierMutation,
): Promise<StayResourceSupplierItem> {
  const response = await httpClient.put<ApiEnvelope<unknown>>(
    stayResourceEndpoints.supplier(spuId, capabilityId),
    mapStayResourceSupplierMutation(payload),
  );
  return mapStayResourceSupplier(
    requireData(response.data, "供应能力更新接口返回了无效数据", isStayResourceSupplierRaw),
  );
}

function requireData<T>(
  envelope: unknown,
  message: string,
  predicate: (value: unknown) => value is T,
): T {
  if (!isRecord(envelope) || envelope.code !== "OK" || !predicate(envelope.data)) {
    throw new Error(message);
  }
  return envelope.data;
}

function requireList<T>(
  envelope: unknown,
  message: string,
  predicate: (value: unknown) => value is T,
): T[] {
  return requireData(
    envelope,
    message,
    (value): value is T[] => Array.isArray(value) && value.every(predicate),
  );
}

function isMediaOrderResult(value: unknown): value is { updated_count: number } {
  return isRecord(value)
    && typeof value.updated_count === "number"
    && Number.isFinite(value.updated_count)
    && value.updated_count >= 0;
}

function isMediaUploadResult(
  value: unknown,
): value is { key: string; file_url: string; media_id: string } {
  return isRecord(value)
    && typeof value.key === "string"
    && typeof value.file_url === "string"
    && typeof value.media_id === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
